import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const countPlaceholders = (text) => (String(text || "").match(/\[(\d*)\]/g) || []).length;

const normalizeAnswers = (answers, amount) => {
    const source = Array.isArray(answers) ? answers : [];
    return Array.from({ length: amount }, (_, idx) => String(source[idx] || ""));
};

const QuestionFormInput = ({ value, onChange }) => {
    const v = value || {};
    const placeholderAmount = countPlaceholders(v.question);
    const answers = normalizeAnswers(v.correctAnswers, placeholderAmount);
    const [helpOpen, setHelpOpen] = useState(false);

    function setQuestion(question) {
        const nextAmount = countPlaceholders(question);
        onChange({
            ...v,
            question,
            correctAnswers: normalizeAnswers(v.correctAnswers, nextAmount),
        });
    }

    function appendPlaceholder() {
        setQuestion(`${v.question || ""}[]`);
    }

    function setAnswer(idx, answer) {
        onChange({
            ...v,
            correctAnswers: answers.map((item, i) => (i === idx ? answer : item)),
        });
    }

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2">Завдання з вводом</Typography>
                <Tooltip title="Показати інструкцію для налаштування цього завдання">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHelpOpen(true)}
                    >
                        Як користуватися
                    </Button>
                </Tooltip>
            </Stack>

            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                helperText="Необов'язково. Показується під назвою модуля."
                fullWidth
            />

            <TextField
                label="Текст завдання"
                value={v.question || ""}
                onChange={(e) => setQuestion(e.target.value)}
                helperText="Вставляйте [] для стандартного поля або [16], щоб задати ширину приблизно у 16 символів."
                fullWidth
                multiline
                minRows={8}
            />

            <Button variant="outlined" onClick={appendPlaceholder}>
                Додати []
            </Button>

            <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Правильні відповіді: {placeholderAmount}
                </Typography>

                {placeholderAmount === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Додайте хоча б один [] у тексті завдання.
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {answers.map((answer, idx) => (
                            <TextField
                                key={idx}
                                label={`Відповідь #${idx + 1}`}
                                value={answer}
                                onChange={(e) => setAnswer(idx, e.target.value)}
                                helperText="Пробіли на початку і в кінці відповіді учня обрізаються."
                                fullWidth
                            />
                        ))}
                    </Stack>
                )}
            </Box>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Як налаштувати завдання з вводом</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            1. У полі "Текст завдання" напишіть текст, який побачить учень.
                        </Typography>
                        <Typography variant="body2">
                            2. У місцях, де учень має ввести слово, вставляйте [].
                        </Typography>
                        <Typography variant="body2">
                            3. Якщо потрібно керувати шириною поля, вкажіть число всередині дужок: [8], [16], [24].
                            Число означає приблизну ширину поля у символах.
                        </Typography>
                        <Typography variant="body2">
                            4. Після кожного пропуску нижче з'явиться поле правильної відповіді. Відповіді заповнюються
                            в тому самому порядку, у якому пропуски стоять у тексті.
                        </Typography>
                        <Typography variant="body2">
                            5. Під час перевірки пробіли на початку і в кінці відповіді учня обрізаються автоматично.
                        </Typography>
                        <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", m: 0 }}>
{`Приклад:
1) Як тебе не любити [16] мій
2) Ім'я поета Шевченка - []
3) [] - то любов, а чорне то [8]`}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default QuestionFormInput;
